import React, { useState, useEffect } from "react";
import { Container, Typography, Grid, Card, CardContent, Button, Checkbox, FormControlLabel, Box, Divider } from "@mui/material";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import SoftBox from "components/SoftBox";
import SoftSnackbar from "components/SoftSnackbar";
import userProfileService from "services/userProfileService";

function AVV() {
  const [checkboxes, setCheckboxes] = useState({
    agb: false,
    datenschutz: false,
    vertragsbedingungen: false,
    haftungsausschluss: false,
  });
  const [userHasAcceptedAVV, setUserHasAcceptedAVV] = useState(false);
  const [loading, setLoading] = useState(false);
  const [snackbar, setSnackbar] = useState({
    open: false,
    message: "",
    color: "info",
  });

  const allChecked = Object.values(checkboxes).every((checked) => checked);

  useEffect(() => {
    // Check if user has already accepted AVV
    const checkAVVStatus = async () => {
      try {
        const profileData = await userProfileService.getProfile();
        if (profileData.avvAccepted) {
          setUserHasAcceptedAVV(true);
          // Pre-fill checkboxes if user has already accepted
          setCheckboxes({
            agb: true,
            datenschutz: true,
            vertragsbedingungen: true,
            haftungsausschluss: true,
          });
        }
      } catch (error) {
        console.error("Error checking AVV status:", error);
      }
    };

    checkAVVStatus();
  }, []);

  const handleDownload = () => {
    // Logic to download AVV PDF - This would be the path to your actual PDF file
    window.open("/assets/documents/avv.pdf", "_blank", "noopener,noreferrer");
  };

  const handleAccept = async () => {
    if (!allChecked) {
      setSnackbar({
        open: true,
        message: "Bitte akzeptieren Sie alle Bedingungen",
        color: "warning",
      });
      return;
    }

    try {
      setLoading(true);
      
      // Save AVV acceptance to backend
      await userProfileService.updateAVVStatus({
        avvAccepted: true,
        acceptedTerms: checkboxes,
      });
      
      setUserHasAcceptedAVV(true);
      setSnackbar({
        open: true,
        message: "AVV erfolgreich akzeptiert",
        color: "success",
      });
    } catch (error) {
      console.error("Error accepting AVV:", error);
      setSnackbar({
        open: true,
        message: "Fehler beim Akzeptieren des AVV",
        color: "error",
      });
    } finally {
      setLoading(false);
    }
  };

  const handleCheckboxChange = (event) => {
    const { name, checked } = event.target;
    setCheckboxes((prev) => ({ ...prev, [name]: checked }));
  };

  const handleCloseSnackbar = () => {
    setSnackbar({ ...snackbar, open: false });
  };

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <Container>
        <SoftBox py={3}>
          <Typography variant="h3" gutterBottom>
            AVV-Vertrag
          </Typography>
          <Typography variant="body1" paragraph>
            Bitte lesen Sie die folgenden Bedingungen sorgfältig durch und bestätigen Sie diese, bevor Sie fortfahren.
          </Typography>
        </SoftBox>

        <Grid container spacing={4}>
          {/* AVV-Beschreibung */}
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  Was ist der AVV?
                </Typography>
                <Typography variant="body2" color="textSecondary">
                  Der Auftragsverarbeitungsvertrag (AVV) regelt die Verarbeitung personenbezogener Daten im Rahmen unserer Dienstleistungen.
                  Gemäß Art. 28 DSGVO ist ein AVV erforderlich, wenn die Verarbeitung personenbezogener Daten im Auftrag durchgeführt wird.
                </Typography>
              </CardContent>
            </Card>
          </Grid>

          {/* Download-Block */}
          <Grid item xs={12}>
            <Card elevation={3}>
              <CardContent>
                <Typography variant="h5" gutterBottom>
                  AVV herunterladen
                </Typography>
                <Typography variant="body2" color="textSecondary" paragraph>
                  Laden Sie hier den Auftragsverarbeitungsvertrag (AVV) als PDF herunter.
                </Typography>
                <Button variant="contained" color="primary" onClick={handleDownload}>
                  AVV herunterladen
                </Button>
                <Divider sx={{ my: 2 }} />
                <Typography
                  variant="h6"
                  color={userHasAcceptedAVV ? "green" : "red"}
                  sx={{ fontWeight: "bold" }}
                >
                  {userHasAcceptedAVV ? "Status: Bestätigt" : "Status: Nicht bestätigt"}
                </Typography>
              </CardContent>
            </Card>
          </Grid>
        </Grid>

        {/* Checkboxes and Accept button */}
        <SoftBox mt={4}>
          <Typography variant="h4" gutterBottom>
            Bestätigungen
          </Typography>
          <Box sx={{ marginTop: "16px" }}>
            <FormControlLabel
              control={
                <Checkbox
                  name="agb"
                  checked={checkboxes.agb}
                  onChange={handleCheckboxChange}
                  disabled={userHasAcceptedAVV}
                />
              }
              label="Ich akzeptiere die AGB."
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="datenschutz"
                  checked={checkboxes.datenschutz}
                  onChange={handleCheckboxChange}
                  disabled={userHasAcceptedAVV}
                />
              }
              label="Ich stimme der Datenschutzerklärung zu."
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="vertragsbedingungen"
                  checked={checkboxes.vertragsbedingungen}
                  onChange={handleCheckboxChange}
                  disabled={userHasAcceptedAVV}
                />
              }
              label="Ich bestätige die Vertragsbedingungen gelesen zu haben."
            />
            <FormControlLabel
              control={
                <Checkbox
                  name="haftungsausschluss"
                  checked={checkboxes.haftungsausschluss}
                  onChange={handleCheckboxChange}
                  disabled={userHasAcceptedAVV}
                />
              }
              label="Ich akzeptiere den Haftungsausschluss."
            />
          </Box>
          <SoftBox display="flex" justifyContent="flex-end" mt={3}>
            <Button
              variant="contained"
              color="success"
              onClick={handleAccept}
              disabled={!allChecked || userHasAcceptedAVV || loading}
            >
              {loading ? "Wird verarbeitet..." : userHasAcceptedAVV ? "Bereits akzeptiert" : "Akzeptieren"}
            </Button>
          </SoftBox>
        </SoftBox>
      </Container>
      <SoftSnackbar
        color={snackbar.color}
        icon="notifications"
        title="Hinweis"
        content={snackbar.message}
        open={snackbar.open}
        onClose={handleCloseSnackbar}
        close={handleCloseSnackbar}
        bgWhite
      />
    </DashboardLayout>
  );
}

export default AVV;
